import React, { useState, useEffect } from 'react';
import './AlarmSettings.css';
import iconBack from '../../assets/images/icon-back.svg';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface AlarmSettingsProps {
  onBack: () => void;
}

const AlarmSettings: React.FC<AlarmSettingsProps> = ({ onBack }) => {
  const { customer } = useAuth();
  const [allNotifications, setAllNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [marketingNotifications, setMarketingNotifications] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  // DB에서 설정 불러오기
  useEffect(() => {
    const loadSettings = async () => {
      if (!customer) {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('customers')
          .select('notification_all, notification_push, notification_marketing')
          .eq('id', customer.id)
          .single();

        if (error) {
          console.error('알림설정 조회 실패:', error);
        } else if (data) {
          setAllNotifications(data.notification_all ?? true);
          setPushNotifications(data.notification_push ?? true);
          setMarketingNotifications(data.notification_marketing ?? true);
        }
      } catch (error) {
        console.error('알림설정 로드 오류:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, [customer]);

  // DB에 설정 저장
  const saveSettings = async (settings: {
    notification_all?: boolean;
    notification_push?: boolean;
    notification_marketing?: boolean;
  }) => {
    if (!customer) return;

    try {
      const { error } = await supabase
        .from('customers')
        .update(settings)
        .eq('id', customer.id);

      if (error) {
        console.error('알림설정 저장 실패:', error);
      }
    } catch (error) {
      console.error('알림설정 저장 오류:', error);
    }
  };

  // 전체 알림 토글
  const handleAllNotificationsToggle = async () => {
    const newValue = !allNotifications;
    setAllNotifications(newValue);
    setPushNotifications(newValue);
    setMarketingNotifications(newValue);

    await saveSettings({
      notification_all: newValue,
      notification_push: newValue,
      notification_marketing: newValue
    });
  };

  // 푸시 알림 토글
  const handlePushToggle = async () => {
    const newValue = !pushNotifications;
    setPushNotifications(newValue);

    // 둘 다 켜져야 전체 ON, 하나라도 꺼지면 전체 OFF
    const newAllValue = newValue && marketingNotifications;
    if (allNotifications !== newAllValue) {
      setAllNotifications(newAllValue);
    }

    await saveSettings({
      notification_all: newAllValue,
      notification_push: newValue
    });
  };

  // 마케팅 알림 토글
  const handleMarketingToggle = async () => {
    const newValue = !marketingNotifications;
    setMarketingNotifications(newValue);

    // 둘 다 켜져야 전체 ON, 하나라도 꺼지면 전체 OFF
    const newAllValue = pushNotifications && newValue;
    if (allNotifications !== newAllValue) {
      setAllNotifications(newAllValue);
    }

    await saveSettings({
      notification_all: newAllValue,
      notification_marketing: newValue
    });
  };

  if (isLoading) {
    return (
      <div className="alarm-settings-container">
        <div className="alarm-settings-header">
          <div className="alarm-settings-header-wrapper">
            <img src={iconBack} alt="뒤로가기" className="header-icon" onClick={onBack} />
            <h1 className="alarm-settings-title">알림설정</h1>
            <div className="header-icon"></div>
          </div>
        </div>
        <div className="alarm-settings-content">
          <p>로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="alarm-settings-container">
      <div className="alarm-settings-header">
        <div className="alarm-settings-header-wrapper">
          <img src={iconBack} alt="뒤로가기" className="header-icon" onClick={onBack} />
          <h1 className="alarm-settings-title">알림설정</h1>
          <div className="header-icon"></div>
        </div>
      </div>

      <div className="alarm-settings-content">
        <h2 className="alarm-settings-page-title">알림설정</h2>
        <div className="alarm-item">
          <div className="alarm-info">
            <h3 className="alarm-name">전체 알림</h3>
            <p className="alarm-description">모든 알림을 한 번에 설정합니다</p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={allNotifications}
              onChange={handleAllNotificationsToggle}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className="alarm-item">
          <div className="alarm-info">
            <h3 className="alarm-name">알림톡 및 SNS 알림</h3>
            <p className="alarm-description">
              가입한 상품의 알림톡 및 SNS 알림을 받습니다
            </p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={pushNotifications}
              onChange={handlePushToggle}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className="alarm-item">
          <div className="alarm-info">
            <h3 className="alarm-name">공지 및 마케팅 이벤트 알림</h3>
            <p className="alarm-description">
              공지사항, 이벤트 및 프로모션 알림을 받습니다
            </p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={marketingNotifications}
              onChange={handleMarketingToggle}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default AlarmSettings;
